import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardCheck, Plus, CheckCircle, Clock, AlertTriangle,
  Trash2, ChevronRight, Calendar, Building2, User, FileText
} from 'lucide-react';

const SHIFT_NAMES = { 1: "Dimineata (06-14)", 2: "Dupa-amiaza (14-22)", 3: "Noapte (22-06)" };

const ShiftHandoverPage = () => {
  const { plants } = useData();
  const { user } = useAuth();
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [stats, setStats] = useState({});
  const [filterPlant, setFilterPlant] = useState('all');

  const [form, setForm] = useState({
    plant_id: '', shift_number: 1,
    handover_date: new Date().toISOString().slice(0, 10),
    observations: '',
    issues: [{ description: '', severity: 'medium' }],
    ongoing_works: [{ description: '', progress_pct: 0 }],
    pending_tasks: [{ description: '', priority: 'medium' }],
    equipment_status: []
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [hRes, sRes] = await Promise.all([
        api.get('/shift-handover/list', { params: { plant_id: filterPlant !== 'all' ? filterPlant : undefined, limit: 50 } }),
        api.get('/shift-handover/stats', { params: { plant_id: filterPlant !== 'all' ? filterPlant : undefined } })
      ]);
      setHandovers(hRes.data);
      setStats(sRes.data);
    } catch (err) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [filterPlant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.plant_id) { toast.error('Selectati fabrica'); return; }
    try {
      const payload = {
        ...form,
        issues: form.issues.filter(i => i.description.trim()),
        ongoing_works: form.ongoing_works.filter(w => w.description.trim()),
        pending_tasks: form.pending_tasks.filter(t => t.description.trim()),
      };
      await api.post('/shift-handover/create', payload);
      toast.success('Registru de schimb creat!');
      setShowCreate(false);
      setForm({ plant_id: '', shift_number: 1, handover_date: new Date().toISOString().slice(0, 10), observations: '', issues: [{ description: '', severity: 'medium' }], ongoing_works: [{ description: '', progress_pct: 0 }], pending_tasks: [{ description: '', priority: 'medium' }], equipment_status: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la creare');
    }
  };

  const handleConfirm = async (id) => {
    try {
      await api.post(`/shift-handover/${id}/confirm`, { observations: '' });
      toast.success('Predare confirmata!');
      fetchData();
      setShowDetail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la confirmare');
    }
  };

  const addListItem = (field) => {
    const defaults = { issues: { description: '', severity: 'medium' }, ongoing_works: { description: '', progress_pct: 0 }, pending_tasks: { description: '', priority: 'medium' } };
    setForm({ ...form, [field]: [...form[field], defaults[field]] });
  };

  const updateListItem = (field, idx, key, val) => {
    const items = [...form[field]];
    items[idx][key] = val;
    setForm({ ...form, [field]: items });
  };

  const removeListItem = (field, idx) => {
    if (form[field].length <= 1) return;
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== idx) });
  };

  const statusColors = { pending_confirmation: 'bg-amber-100 text-amber-700', confirmed: 'bg-emerald-100 text-emerald-700', draft: 'bg-slate-100 text-slate-600' };
  const statusLabels = { pending_confirmation: 'Asteapta confirmare', confirmed: 'Confirmat', draft: 'Ciorna' };
  const sevColors = { low: 'bg-slate-100 text-slate-600', medium: 'bg-blue-100 text-blue-600', high: 'bg-amber-100 text-amber-600', critical: 'bg-red-100 text-red-600' };

  return (
    <div className="space-y-6" data-testid="shift-handover-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Registru Electronic de Schimb</h1>
          <p className="text-slate-600 mt-1">Predare-primire tura ({handovers.length} inregistrari)</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filterPlant} onValueChange={setFilterPlant}>
            <SelectTrigger className="w-48 h-10"><Building2 className="w-4 h-4 mr-2" /><SelectValue placeholder="Fabrica" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate fabricile</SelectItem>
              {plants?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="btn-primary h-10" onClick={() => setShowCreate(true)} data-testid="create-handover-btn">
            <Plus className="w-4 h-4 mr-2" /> PREDARE TURA
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-xs text-slate-500">Total</p></div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold">{stats.confirmed || 0}</p><p className="text-xs text-slate-500">Confirmate</p></div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold">{stats.pending_confirmation || 0}</p><p className="text-xs text-slate-500">Asteapta confirmare</p></div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Calendar className="w-5 h-5 text-indigo-600" /></div>
          <div><p className="text-2xl font-bold">{stats.today_count || 0}</p><p className="text-xs text-slate-500">Azi</p></div>
        </CardContent></Card>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052CC]" /></div>
      ) : (
        <div className="space-y-2">
          {handovers.length === 0 && <div className="text-center py-12 text-slate-500"><ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Niciun registru de schimb</p></div>}
          {handovers.map(h => (
            <Card key={h.id} className="border-0 shadow-sm hover:shadow-md cursor-pointer transition-shadow" onClick={() => setShowDetail(h)} data-testid={`handover-card-${h.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <ClipboardCheck className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{h.plant_name}</span>
                        <Badge variant="outline" className="text-xs">{SHIFT_NAMES[h.shift_number] || `Tura ${h.shift_number}`}</Badge>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[h.status] || ''}`}>{statusLabels[h.status] || h.status}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{h.handover_date}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />Predare: {h.outgoing_user_name}</span>
                        {h.incoming_user_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />Primire: {h.incoming_user_name}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.issues?.filter(i => i.description).length > 0 && <Badge variant="destructive" className="text-xs">{h.issues.filter(i => i.description).length} probleme</Badge>}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Predare Tura - Registru de Schimb</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fabrica *</Label>
                <Select value={form.plant_id} onValueChange={v => setForm({ ...form, plant_id: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                  <SelectContent>{plants?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tura *</Label>
                <Select value={String(form.shift_number)} onValueChange={v => setForm({ ...form, shift_number: parseInt(v) })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tura 1 - Dimineata (06-14)</SelectItem>
                    <SelectItem value="2">Tura 2 - Dupa-amiaza (14-22)</SelectItem>
                    <SelectItem value="3">Tura 3 - Noapte (22-06)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={form.handover_date} onChange={e => setForm({ ...form, handover_date: e.target.value })} className="h-11" />
              </div>
            </div>

            {/* Issues */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Probleme aparute in tura</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('issues')}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.issues.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input placeholder="Descriere problema..." value={item.description} onChange={e => updateListItem('issues', idx, 'description', e.target.value)} className="flex-1 h-9" />
                  <Select value={item.severity} onValueChange={v => updateListItem('issues', idx, 'severity', v)}>
                    <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Scazuta</SelectItem>
                      <SelectItem value="medium">Medie</SelectItem>
                      <SelectItem value="high">Ridicata</SelectItem>
                      <SelectItem value="critical">Critica</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.issues.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeListItem('issues', idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                </div>
              ))}
            </div>

            {/* Ongoing works */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-blue-600 flex items-center gap-1"><Clock className="w-4 h-4" /> Lucrari in derulare</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('ongoing_works')}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.ongoing_works.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input placeholder="Descriere lucrare..." value={item.description} onChange={e => updateListItem('ongoing_works', idx, 'description', e.target.value)} className="flex-1 h-9" />
                  <Input type="number" placeholder="%" min={0} max={100} value={item.progress_pct} onChange={e => updateListItem('ongoing_works', idx, 'progress_pct', parseInt(e.target.value) || 0)} className="w-20 h-9" />
                  <span className="text-xs text-slate-500">%</span>
                  {form.ongoing_works.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeListItem('ongoing_works', idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                </div>
              ))}
            </div>

            {/* Pending tasks */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-amber-600 flex items-center gap-1"><FileText className="w-4 h-4" /> Sarcini ramase de executat</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('pending_tasks')}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.pending_tasks.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input placeholder="Descriere sarcina..." value={item.description} onChange={e => updateListItem('pending_tasks', idx, 'description', e.target.value)} className="flex-1 h-9" />
                  <Select value={item.priority} onValueChange={v => updateListItem('pending_tasks', idx, 'priority', v)}>
                    <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Scazuta</SelectItem>
                      <SelectItem value="medium">Medie</SelectItem>
                      <SelectItem value="high">Ridicata</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.pending_tasks.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeListItem('pending_tasks', idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Observatii generale</Label>
              <Textarea value={form.observations} onChange={e => setForm({ ...form, observations: e.target.value })} rows={3} placeholder="Stare generala echipamente, incidente, altele..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleCreate} data-testid="submit-handover-btn">Predare Tura</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Detalii Registru de Schimb</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg">
                <div><p className="text-xs text-slate-500">Fabrica</p><p className="font-semibold">{showDetail.plant_name}</p></div>
                <div><p className="text-xs text-slate-500">Tura</p><p className="font-semibold">{showDetail.shift_name}</p></div>
                <div><p className="text-xs text-slate-500">Data</p><p className="font-semibold">{showDetail.handover_date}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">PREDARE</p>
                  <p className="font-semibold text-slate-900">{showDetail.outgoing_user_name}</p>
                  <p className="text-xs text-slate-500">{showDetail.outgoing_signature_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
                <div className={`p-3 rounded-lg ${showDetail.incoming_user_name ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <p className={`text-xs font-medium ${showDetail.incoming_user_name ? 'text-emerald-600' : 'text-amber-600'}`}>PRIMIRE</p>
                  <p className="font-semibold text-slate-900">{showDetail.incoming_user_name || 'Neconfirmat'}</p>
                  {showDetail.incoming_signature_at && <p className="text-xs text-slate-500">{showDetail.incoming_signature_at?.slice(0, 16).replace('T', ' ')}</p>}
                </div>
              </div>

              {showDetail.issues?.filter(i => i.description).length > 0 && (
                <div><p className="font-medium text-red-600 text-sm mb-2">Probleme aparute</p>
                  {showDetail.issues.filter(i => i.description).map((i, idx) => (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${sevColors[i.severity] || ''}`}>{i.severity}</span>
                      <span className="text-sm">{i.description}</span>
                    </div>
                  ))}
                </div>
              )}
              {showDetail.ongoing_works?.filter(w => w.description).length > 0 && (
                <div><p className="font-medium text-blue-600 text-sm mb-2">Lucrari in derulare</p>
                  {showDetail.ongoing_works.filter(w => w.description).map((w, idx) => (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <Badge variant="outline" className="text-xs">{w.progress_pct}%</Badge>
                      <span className="text-sm">{w.description}</span>
                    </div>
                  ))}
                </div>
              )}
              {showDetail.pending_tasks?.filter(t => t.description).length > 0 && (
                <div><p className="font-medium text-amber-600 text-sm mb-2">Sarcini ramase</p>
                  {showDetail.pending_tasks.filter(t => t.description).map((t, idx) => (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${sevColors[t.priority] || ''}`}>{t.priority}</span>
                      <span className="text-sm">{t.description}</span>
                    </div>
                  ))}
                </div>
              )}
              {showDetail.observations && <div><p className="font-medium text-sm mb-1">Observatii</p><p className="text-sm text-slate-600">{showDetail.observations}</p></div>}
            </div>
            <DialogFooter>
              {showDetail.status === 'pending_confirmation' && (
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleConfirm(showDetail.id)} data-testid="confirm-handover-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Confirm Primirea Turei
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowDetail(null)}>Inchide</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ShiftHandoverPage;
